import type { ReactElement } from "react";
import { ContentReviewDisplay } from "../../components/ContentReviewDisplay";

interface ReviewStepProps {
  formData: {
    title?: string;
    releaseYear?: number;
    runtime?: number;
    synopsis?: string;
    director?: string;
    cast?: string[];
    crew?: Array<{ name: string; job: string; department?: string }>;
    genres?: string[];
    rating?: string;
    posterUrl?: string;
    backdropUrl?: string;
    horizontalTileUrl?: string;
    verticalTileUrl?: string;
    featureVideoUrl?: string;
    trailerVideoUrl?: string;
  };
  submissionId?: string | null; // For submission mode
  titleId?: string | null; // For title mode
  submitting: boolean;
  error?: string | null;
  mode?: "submission" | "title"; // Which workflow we're in
}

/**
 * Review Step - Preview all data before submission
 */
export function ReviewStep({
  formData,
  submissionId,
  titleId,
  submitting: _submitting,
  error = null,
  mode: _mode = "submission",
}: Readonly<ReviewStepProps>): ReactElement {
  return (
    <div className="pb-12">
      {/* Header - matches ReviewSubmissionPage styling exactly */}
      <div className="mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">{formData.title || "Review & Submit"}</h1>
          <p className="text-neutral-400 mt-1">Review all the information before submitting for admin approval.</p>
        </div>
      </div>

      {/* Main Content Area - matches ReviewSubmissionPage structure */}
      <div className="space-y-8">
        {/* Use shared review display component */}
        <ContentReviewDisplay
          formData={formData}
          submissionId={submissionId}
          titleId={titleId}
          showVideoStatus={true}
          showAssets={true}
          showSubtitles={true}
        />

        {/* Error message */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-400">Submission Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
